import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import { getUserTenantsByUserIdQueryFn, useUserTenantsByUserIdQuery } from '@/api/auth/queries';
import { authKeys } from '@/api/auth/keys';
import { Card, Typography, Avatar, Tag, Space, Button, Descriptions, Row, Col, Divider, Flex, Empty, Alert, Result, theme } from 'antd';
import { UserOutlined, PlusOutlined, PartitionOutlined } from '@ant-design/icons';
import { IconCardTitle } from '@/components/ui/IconCardTitle';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { UserRoleEditor } from '@/components/tenant/UserRoleEditor';
import { UserEmailVerificationActions, isUserEmailVerified } from '@/components/admin/UserEmailVerificationActions';
import { AssignTenantModal, UserTenantAccessCard } from '@/components/tenant';
import { useAuth } from '@/hooks/useAuth';
import { useAllTenantsWithCompanies } from '@/hooks/useAllTenantsWithCompanies';
import { canEditGlobalUserRole, isAdminEquivalentRole } from '@/utils/authz';
import { getUserDisplayName, getUserInitial } from '@/utils/userDisplay';
import { formatDateTime } from '@/utils/dateFormat';
import { groupMembershipsByTenant } from '@/utils/tenantMemberships';

const { Text } = Typography;

export const Route = createFileRoute('/admin/users/$userId')({
  component: UserDetailPage,
  loader: async ({ context, params }) => {
    await context.queryClient.prefetchQuery({
      queryKey: authKeys.userTenantsByUserId(params.userId),
      queryFn: () => getUserTenantsByUserIdQueryFn(params.userId)
    });
  }
});

function UserDetailPage() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { payload } = useAuth();
  const canEditRoles = canEditGlobalUserRole(payload?.role);
  const canManageTenants = isAdminEquivalentRole(payload?.role);
  const { userId } = Route.useParams();
  const { data, isLoading, error } = useUserTenantsByUserIdQuery(userId);
  const { tenants, tenantsById } = useAllTenantsWithCompanies();
  const [assignOpen, setAssignOpen] = useState(false);
  const user = data?.user;
  // Admins/developers reach every tenant through their global role and are never assigned to one
  const isGlobalStaffUser = isAdminEquivalentRole(user?.role);

  const tenantNames = useMemo(() => new Map(tenants.map((tenant) => [tenant.id, tenant.name])), [tenants]);
  const tenantGroups = useMemo(() => groupMembershipsByTenant(data, tenantNames), [data, tenantNames]);
  const memberTenantIds = useMemo(() => (data?.clientMemberships ?? []).map((membership) => membership.clientId), [data]);

  const header = (
    <DetailPageHeader
      icon={<UserOutlined />}
      title={user?.name || user?.email || t('admin:users.detail.title')}
      meta={
        user ? (
          <>
            <Text type="secondary">{user.email}</Text>
            <Tag style={{ marginInlineEnd: 0 }}>{t(`admin:users.roleOptions.${user.role}`, { defaultValue: user.role })}</Tag>
          </>
        ) : (
          <Text type="secondary">{t('admin:users.detail.subtitle')}</Text>
        )
      }
      backTo="/admin/users"
      backLabel={t('admin:users.detail.backToUsers')}
    />
  );

  if (error) {
    return (
      <PageTransition animation="fadeInUp">
        {header}
        <Card>
          <Result
            status="warning"
            title={t('admin:users.detail.title')}
            subTitle={t('admin:users.detail.errorLoading', { error: error.message })}
          />
        </Card>
      </PageTransition>
    );
  }

  return (
    <PageTransition animation="fadeInUp">
      {header}

      <Row gutter={[token.marginMD, token.marginMD]}>
        <Col xs={24} lg={8}>
          <Card
            loading={isLoading}
            title={<IconCardTitle icon={<UserOutlined />} title={t('admin:users.detail.profile')} />}
          >
            {user ? (
              <Space orientation="vertical" size={token.marginMD} style={{ width: '100%' }}>
                <Flex align="center" gap={token.marginSM} style={{ minWidth: 0 }}>
                  <Avatar size={token.controlHeightLG * 1.5} src={user.picture || undefined} style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}>
                    {!user.picture ? getUserInitial(user) : null}
                  </Avatar>
                  <Flex vertical style={{ minWidth: 0 }}>
                    <Text strong ellipsis={{ tooltip: true }} style={{ fontSize: token.fontSizeLG }}>{user.name || '—'}</Text>
                    <Text type="secondary" ellipsis={{ tooltip: true }}>{user.email}</Text>
                  </Flex>
                </Flex>

                <Divider style={{ margin: 0 }} />

                <div>
                  <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>{t('admin:users.detail.globalRole')}</Text>
                  <UserRoleEditor
                    userId={user.id}
                    currentRole={user.role}
                    currentUserId={payload?.userId}
                    userName={getUserDisplayName(user)}
                    canEdit={canEditRoles}
                  />
                </div>

                <Divider style={{ margin: 0 }} />

                <div>
                  <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>{t('admin:users.detail.verificationStatus')}</Text>
                  <Space wrap>
                    <Tag color={isUserEmailVerified(user.emailVerified) ? 'green' : 'orange'}>
                      {isUserEmailVerified(user.emailVerified) ? t('admin:users.emailVerified') : t('admin:users.emailUnverified')}
                    </Tag>
                    {user.phoneNumber ? (
                      <Tag color={user.phoneVerified ? 'green' : 'default'}>{user.phoneVerified ? t('admin:users.phoneVerified') : t('admin:users.phoneUnverified')}</Tag>
                    ) : null}
                  </Space>
                  <UserEmailVerificationActions
                    userId={user.id}
                    email={user.email}
                    emailVerified={user.emailVerified}
                    userName={getUserDisplayName(user)}
                    variant="detail"
                  />
                </div>

                <Divider style={{ margin: 0 }} />

                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  items={[
                    {
                      key: 'userId',
                      label: t('admin:users.detail.userId'),
                      children: <Text code copyable style={{ wordBreak: 'break-all' }}>{user.id}</Text>
                    },
                    {
                      key: 'createdAt',
                      label: t('admin:users.detail.createdAt'),
                      children: formatDateTime(user.createdAt)
                    },
                    {
                      key: 'updatedAt',
                      label: t('admin:users.detail.updatedAt'),
                      children: formatDateTime(user.updatedAt)
                    }
                  ]}
                />
              </Space>
            ) : (
              <Text type="secondary">{isLoading ? t('admin:users.detail.loading') : t('admin:users.detail.userNotFound')}</Text>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card
            loading={isLoading}
            title={
              <IconCardTitle
                icon={<PartitionOutlined />}
                title={t('admin:users.detail.tenantAccess.title')}
                extra={!isLoading && <Tag style={{ marginInlineEnd: 0 }}>{tenantGroups.length}</Tag>}
              />
            }
            extra={
              canManageTenants &&
              user &&
              !isGlobalStaffUser && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignOpen(true)}>
                  {t('admin:users.detail.tenantAccess.assign')}
                </Button>
              )
            }
          >
            {isGlobalStaffUser && tenantGroups.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space orientation="vertical" size={4}>
                    <Text>{t('admin:users.detail.tenantAccess.staffTitle')}</Text>
                    <Text type="secondary">{t('admin:users.detail.tenantAccess.staffDescription')}</Text>
                  </Space>
                }
              />
            ) : tenantGroups.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space orientation="vertical" size={4}>
                    <Text>{t('admin:users.detail.tenantAccess.emptyTitle')}</Text>
                    <Text type="secondary">{t('admin:users.detail.tenantAccess.emptyDescription')}</Text>
                  </Space>
                }
              >
                {canManageTenants && user && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignOpen(true)}>
                    {t('admin:users.detail.tenantAccess.assign')}
                  </Button>
                )}
              </Empty>
            ) : (
              <Flex vertical gap={token.margin}>
                {isGlobalStaffUser && (
                  <Alert type="info" showIcon title={t('admin:users.detail.tenantAccess.staffKeptNote')} />
                )}
                {tenantGroups.map((group) => (
                  <UserTenantAccessCard
                    key={group.clientId}
                    userId={userId}
                    userName={user?.name || user?.email || ''}
                    clientId={group.clientId}
                    clientName={group.clientName}
                    tenantRole={group.tenantRole}
                    companies={group.companies}
                    tenantCompanies={tenantsById.get(group.clientId)?.companies ?? []}
                    canManage={canManageTenants && !isGlobalStaffUser}
                  />
                ))}
              </Flex>
            )}
          </Card>
        </Col>
      </Row>

      {user && (
        <AssignTenantModal
          open={assignOpen}
          onCancel={() => setAssignOpen(false)}
          userId={userId}
          userName={user.name || user.email || ''}
          excludeTenantIds={memberTenantIds}
        />
      )}
    </PageTransition>
  );
}
